/**
 * Safe trend / value-change helpers for the Daily Insights Post-Update card
 * (Revenue, Downloads, R/D ratio).
 *
 * Mirrors the agreed behavior in `UpdateImpactAggregates` (GR-1985):
 * - both periods zero  → 0 (no change)
 * - base zero, current positive  → +1 (+100%)
 * - current zero, base positive  → -1 (-100%)
 * - one period has zero downloads (asymmetric)  → R/D value change is `undefined`
 *   so the UI can render an explicit N/A indicator.
 */

/**
 * Compute the percentage trend between `current` and `base` while handling
 * the edge cases where one of the periods has zero revenue / downloads.
 */
export const computeTrend = (current: number, base: number): number => {
  if (!Number.isFinite(current) || !Number.isFinite(base)) return 0
  if (!base) {
    if (!current) return 0
    return current > 0 ? 1 : -1
  }
  const result = current / base - 1
  return Number.isFinite(result) ? result : 0
}

/**
 * Trend of the revenue/downloads ratio. A period with zero downloads has an
 * undefined ratio — treat it as 0 so `computeTrend` yields ±1 in asymmetric
 * cases and 0 when both periods are empty.
 */
export const computeRevenueDownloadsRatioTrend = (afterRevenue: number, afterDownloads: number, beforeRevenue: number, beforeDownloads: number): number => {
  const ratio = afterDownloads ? afterRevenue / afterDownloads : 0
  const baseRatio = beforeDownloads ? beforeRevenue / beforeDownloads : 0
  return computeTrend(ratio, baseRatio)
}

/**
 * Change in the revenue/downloads ratio (currency-typed value).
 * Returns `undefined` when exactly one period has zero downloads (the change
 * is mathematically undefined and should render as N/A). Returns 0 when both
 * periods have zero downloads (symmetric — no data on either side).
 */
export const computeRevenueDownloadsRatioChange = (
  afterRevenue: number,
  afterDownloads: number,
  beforeRevenue: number,
  beforeDownloads: number
): number | undefined => {
  if (![afterRevenue, afterDownloads, beforeRevenue, beforeDownloads].every(Number.isFinite)) return undefined
  if (afterDownloads === 0 && beforeDownloads === 0) return 0
  if (afterDownloads === 0 || beforeDownloads === 0) return undefined
  return afterRevenue / afterDownloads - beforeRevenue / beforeDownloads
}
